import Vue from 'vue';
import _ from 'lodash';
import { mapGetters } from 'vuex';
import { cssGenerator } from '@/mixins/css-generator';
import { editHistory } from '@/mixins/history';
import tooltip from '@/mixins/components/tooltip';
import droplets from '@editor/droplets/droplets.config.json';
import grid from '@editor/droplets/grid/droplet.schema';
import { legacyChecks } from './mixins/legacy-checks';
import styles from './styles';
import InputSlider from './options/input-slider';
import InputNumber from './options/input-number';
import ColorPicker from './options/color-picker';
import SelectOptions from './options/select';
import FontSelector from './options/font-selector';
import ShadowSet from './options/shadow-set';
import FlexCenterContent from './options/section-options/flex-center-content';
import Checkbox from './options/checkbox';
import Flexbox from './options/flexbox';
import BackgroundImages from './options/background-images';
import BackgroundOptions from './options/background-options';
import ShapeDividers from './options/section-options/shape-dividers';
import VideoLayer from './options/section-options/video-layer';

export default Vue.component('style-sidebar', {
  template: require('./template.html'),
  name: 'style-sidebar',
  mixins: [cssGenerator, legacyChecks, editHistory],
  components: {
    tooltip,
  },
  computed: {
    ...mapGetters([
      'showStyleSidebar',
      'activeDroplet',
      'activeDropletItem',
      'viewport',
      'dropletsTab',
      'currentSite',
      'currentPage',
      'styleConditionals',
    ]),
    styles() {
      return styles;
    },
    states() {
      let states = [{ label: 'Default', value: '' }];
      const activeConfig = this.dropletConfig.components[this.activeComponent];
      if (this.viewport === 'desktop') {
        states.push({ label: 'Hover', value: ':hover' });
      }
      if (activeConfig.addedStates) {
        states = states.concat(activeConfig.addedStates);
      }
      return states;
    },
    shouldHide() {
      const activeConfig = this.dropletConfig.components[this.activeComponent];
      return activeConfig.hiddenViewports && activeConfig.hiddenViewports.includes(this.viewport);
    },
    components() {
      let list = {};
      if (this.activeDroplet._id) {
        const styleConfig = this.dropletConfig;
        if (styleConfig) {
          list = styleConfig.components || {};
        }
      }
      return list;
    },
    dropletLabel() {
      const name = this._dropletType(this.activeDroplet).name;
      return droplets[name].label;
    },
    componentConfig() {
      return this.dropletConfig && this.activeComponent ?
        this.dropletConfig.components[this.activeComponent] : {};
    },
    dropletConfig() {
      return this.styles[this._dropletType(this.activeDroplet).name];
    },
    warning() {
      const activeConfig = this.dropletConfig.components[this.activeComponent];
      return activeConfig && activeConfig.warning ? activeConfig.warning : false;
    },
    activeGridItem() {
      const droplet = this.activeDroplet._id ? this.activeDroplet : {};
      const active = this.activeDropletItem._id && this.activeComponent === 'column';
      const defaultState = this.activeState === '';
      return droplet.type === grid().type && active && defaultState;
    },
    styleOptions() {
      let options = [];
      if (this.activeComponent) {
        const styleConfig = this.dropletConfig;
        if (styleConfig) {
          const compConfig = styleConfig.components ?
            styleConfig.components[this.activeComponent] : false;
          options = compConfig && compConfig.options ? compConfig.options : [];
        }
      }
      return options;
    },
    options() {
      return {
        color: ColorPicker,
        slider: InputSlider,
        number: InputNumber,
        select: SelectOptions,
        shadowset: ShadowSet,
        checkbox: Checkbox,
        flexbox: Flexbox,
        fontselector: FontSelector,
        backgroundimages: BackgroundImages,
        flexcentercontent: FlexCenterContent,
        shapedividers: ShapeDividers,
        videolayer: VideoLayer,
        backgroundoptions: BackgroundOptions,
      };
    },
    isSection() {
      return this.activeDroplet.type === 'sectionBlock';
    },
  },
  data() {
    return {
      activeComponent: null,
      activeState: '',
      debTime: 250,
      initialDroplet: null,
      hasChanges: false,
      styleAll: false,
      display: {
        transition: 0,
        transition_unit: '',
      },
      grid: {
        colStart: 0,
        colEnd: 0,
        rowStart: 0,
        rowEnd: 0,
      },
      domNodes: {},
    };
  },
  watch: {
    'activeDroplet._id': function check() {
      this.activeComponent = null;
      this.activeState = '';
      this.domNodes = {};
      if (this.activeDroplet._id) {
        this.setDomNodes();
        // send tracking event
        this._trackEvent({
          type: 'open_style_droplet',
          meta: {
            site_name: this.currentSite.alias,
            site_type: this.currentSite.type,
            site_tier: this.currentSite.tier,
            page_name: this.currentPage.name,
            page_type: this.currentPage.type,
            droplet_type: this.activeDroplet.type,
          },
        });
      }
      // this is needed for droplet plugins that may have resize event listeners
      // we want them to have a simulated resize trigger
      $(window).trigger('resize');
    },
    activeComponent(component) {
      if (component) {
        this.initialDroplet = _.cloneDeep(this.activeDroplet);
        this.updateHasChanges(false);
      } else {
        this.initialDroplet = null;
        this.$store.dispatch('setActiveDropletItem', {});
      }
    },
    activeDropletItem: {
      handler(item) {
        this.styleAll = typeof item._id === 'undefined';
        if (this.activeGridItem) {
          this.getGridAreas();
        }
      },
      deep: true,
    },
    viewport() {
      this.setActiveState('');
      if (this.activeGridItem) {
        this.getGridAreas();
      }
    },
    dropletsTab(open) {
      if (open && this.activeDroplet._id) {
        this.$store.dispatch('setActiveDroplet', { droplet: {}, items: [] });
      }
    },
    activeState(state) {
      if (this.activeDroplet._id) {
        const styling = this.styles[this._dropletType(this.activeDroplet).name];
        const activeConfig = styling.components[this.activeComponent];
        if (activeConfig && activeConfig.selectors.length > 0) {
          const $vm = {
            activeComponent: this.activeComponent,
            viewport: this.viewport,
            droplet: this.activeDroplet,
          };
          this.display.transition = this._getValTransition({ $vm, styling });
        }
      }
      this.$store.dispatch('setActiveState', state);
    },
  },
  methods: {
    setDomNodes() {
      const row = document.getElementById(`drp-${this.activeDroplet._id}`);
      this.$set(this.domNodes, '$dropletRow', row);
    },
    setActiveComponent(c) {
      if (this.activeDroplet.bounceDroplet) {
        return;
      }
      this.activeComponent = c;
      this.styleAll = typeof this.activeDropletItem._id === 'undefined';
      if (!c) {
        this.activeState = '';
      }
    },
    updateTransitions() {
      const styling = this.styles[this._dropletType(this.activeDroplet).name];
      const $vm = {
        activeComponent: this.activeComponent,
        activeState: this.activeState,
        viewport: this.viewport,
        droplet: this.activeDroplet,
        display: this.display,
      };
      this._updateTransitions($vm, styling, this);
    },
    closeSideBar() {
      this.setEditHistory({
        type: 'droplet_styles',
        meta: {
          dropletType: this.activeDroplet.type,
        },
      });
      this.setActiveDroplet({ droplet: {}, items: [] });
    },
    async cancelChanges() {
      if (this.hasChanges) {
        this.$log.info('revertDroplet from side style bar');
        const currentPage = this.currentPage._id === this.activeDroplet._id;
        const revert = {
          drp: this.activeDroplet,
          initialState: this.initialDroplet,
        };
        if (currentPage) {
          revert.items = this.currentSite.recycled;
          revert.index = revert.items.findIndex(d => d._id === revert.drp._id);
        }
        const droplet = await this.$store.dispatch('revertDroplet', revert);

        if (currentPage) {
          await this.$store.dispatch('setCanvasPage', {
            drp: droplet,
          });
        }
        // we utilize a bounce technique to force a re-render so that
        // the dragula directive can get the updated state
        // we need to exclude the code droplet as there is a weird template
        // condition that does not allow the overlay bubble to move back in
        // during the bounce
        this.setActiveDroplet(droplet);
        this.activeComponent = null;
        if (this.activeDroplet.type !== 'droplet.codeGeneral') {
          await this.$store.dispatch('bounceDroplet', { drp: droplet });
          this.setDomNodes();
        }
      }
    },
    setActiveDroplet(droplet) {
      this.$store.dispatch('setActiveDroplet', { droplet });
    },
    setActiveDropletItem(e, item) {
      if (item.removed) {
        return;
      }
      const checked = e.target.checked;
      if (checked) {
        this.$store.dispatch('setActiveDropletItem', item);
      } else {
        this._debounce(() => {
          this.$store.dispatch('setActiveDropletItem', {});
        }, 200);
      }
    },
    setActiveState(v) {
      this.activeState = v;
    },
    showPreview() {
      this.$store.dispatch('setAnimationPreview');
    },
    callCssGenerator(params, plugin) {
      const root = this.domNodes.$dropletRow;
      const payload = {
        styleEl: root.parentNode,
        $vm: this.activeDroplet,
      };
      if (!params) {
        payload.drp = this.activeDroplet;
      }
      if (params) {
        payload[params.key] = params.value;
      }
      this.generateCSS(payload);
      // if component needs to reload a jquery plugin after style changes,
      // we need to allow this since the DOM changes
      if (plugin) {
        this.$store.dispatch('callPluginBounce', this.activeDroplet._id);
      }
    },
    updateHasChanges(val) {
      this.hasChanges = val;
    },
    setObserver() {
      this.$store.dispatch('setDropletObserver', this.activeDroplet._id);
    },
    getList(params) {
      const keys = params.key.split('.');
      if (keys.length < 2) {
        return params.obj[params.key];
      }
      const list = [];
      _.forEach(params.obj[keys[0]], (upper) => {
        _.forEach(upper[keys[1]], (item) => {
          list.push(item);
        });
      });
      return list;
    },
    notHidden(component) {
      let show = true;
      const conditions = component.conditions;
      if (conditions && _.isArray(conditions)) {
        const hidden = _.filter(
          conditions, c => !this.styleConditionals[this.activeDroplet._id][c],
        );
        show = hidden.length < 1;
      }
      return show;
    },
    getTitle(html) {
      const wrap = document.createElement('div');
      wrap.innerHTML = html;
      return wrap.textContent;
    },
    generateGridCss() {
      const grid = this.activeDroplet;
      const columns = [];
      _.forEach(grid.rows, row => _.forEach(row.columns, column => columns.push(column)));
      const root = this.domNodes.$dropletRow;
      return this.generateCSS({
        styleEl: root.parentNode,
        $vm: this.activeDroplet,
        drps: [...columns, grid],
      });
    },
    updateGridAreas() {
      this._debounce(() => {
        const rule = _.find(this.activeDropletItem.mediaQueries, mq => mq.property === 'grid-area');
        if (rule) {
          const val = _.cloneDeep(this.grid);
          // this.$store.dispatch('setDropletVal',
          // { drp: this.activeDropletItem, key: 'grid', val });
          this.$store.dispatch('changeDropletCss', {
            drp: this.activeDroplet,
            val: `${val.rowStart} / ${val.colStart} / ${val.rowEnd} / ${val.colEnd}`,
            parent: rule,
            viewport: this.viewport,
          });
          this.generateGridCss();
          this.updateHasChanges(true);
        }
      }, 250);
    },
    getGridArea({ rule, area }) {
      const grid = area;
      const gridSet = /^\s*\d+\s*\/\s*\d+\s*\/\s*\d+\s*\/\s*\d+\s*$/;
      const getDesktop = rule[this.viewport] === 'default' && this.viewport !== 'desktop' && rule.desktop.match(gridSet);
      if (rule[this.viewport].match(gridSet) || getDesktop) {
        const areas = rule[getDesktop ? 'desktop' : this.viewport].split(' / ');
        grid.colStart = areas[1];
        grid.colEnd = areas[3];
        grid.rowStart = areas[0];
        grid.rowEnd = areas[2];
      }
      return grid;
    },
    getGridAreas() {
      const rule = _.find(this.activeDropletItem.mediaQueries, mq => mq.property === 'grid-area');
      const { grid } = this.activeDropletItem;
      let area = _.cloneDeep(grid);
      if (rule) {
        area = this.getGridArea({ rule, area });
      }
      this.grid = {
        colStart: area.colStart,
        colEnd: area.colEnd,
        rowStart: area.rowStart,
        rowEnd: area.rowEnd,
      };
    },
    optionAvailable(option) {
      const itemCleared = !this.activeDropletItem._id ||
      (this.activeDropletItem._id && (!this.componentConfig.itemList ||
      (this.componentConfig.itemList &&
       this.componentConfig.itemList.options.includes(option.name))));
      return itemCleared && (!option.hiddenStates ||
      (option.hiddenStates && !option.hiddenStates.includes(this.activeState)));
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/style-sidebar/index.js